import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Snackbar } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { useActions } from "../../hooks/useActions";
import { useSelector } from "react-redux";
import { IExpression } from "../../models/configuration/plugin/IConfiguration";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { RootState, store } from "../../state/store";
import ExpressionsControl from "../../components/shared/config/ExpressionsControl";
import "../../assets/style.css";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { ActionType } from "../../state/actions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const ConfigSendValidations: React.FC = () => {

    const { saveConfigSendValidationsData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const configId = useSelector((state: RootState) => state.appSlice.configId);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);

    // Send Validations State
    const [sendValidations, setSendValidations] = React.useState<IExpression[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [saveError, setSaveError] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [saving, setSaving] = useState<boolean>(false);

    // Loading Data
    const hasFetchedData = useRef(false);
    const { data, error, loading } = useConfigurationData(ConfigurationDataType.SEND_VALIDATIONS);
    const mData = useMemo(() => data, [data]);

    useEffect(() => {
        if (!hasFetchedData.current && data) {
            setSelectedConfigId(configId ?? '');
            setSendValidations(mData);
            hasFetchedData.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const openAlert = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }, []);

    // Handling Loading Error 
    useEffect(() => {
        if (error) {
            setErrorMessage(error);
            openAlert(`Error loading data: ${error}`, 'error');
        }
    }, [error, openAlert]);

    // Handling Saving Error 
    useEffect(() => {
        if (!saveError && data !== null) {
            setSendValidations(mData);
        }
    }, [data, saveError, mData]);

    const updateSendValidations = (updatedSendValidations: IExpression[]) => {
        setSendValidations(updatedSendValidations);
    }

    // Call this to save local session redux state
    const saveState = useCallback(() => {
        try {
            const currentState = store.getState();
            const configPlugInData = currentState.configPlugInData.data;
            const updatedConfigPlugInData = {
                ...configPlugInData,
                Configuration: {
                    ...configPlugInData?.Configuration,
                    SendValidations: sendValidations
                },
            };
            store.dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                payload: updatedConfigPlugInData
            });

        } catch (e) {
            console.log("Error Saving State: ", e);
            openAlert(`Error Saving State: ${e}`, 'error');
        }
    }, [openAlert, sendValidations]);

    // Save state whenever sendValidations changes after initial load
    useEffect(() => {
        if (hasFetchedData.current && sendValidations.length > 0) {
            saveState();
        }
    }, [saveState, sendValidations]);

    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const cId = selectedConfigId || configId || '';
            const didSave = Boolean(await saveConfigSendValidationsData(cId, sendValidations, accessToken));
            const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
            const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
            const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
            const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
            const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
            openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
            setSaveError(false);
            setErrorMessage('');
        } catch (e) {
            const error = e instanceof Error ? e.message : `${e}`;
            setSaveError(true);
            setErrorMessage(error);
            openAlert(`Failed to save data: ${error}`, 'error');
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    // Alert Handling 
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    return (
        <>
            <section>
                <div>
                    {(loading || saving) && <ConfigLoad loading={loading || saving} />}
                </div>
                <div></div>

                <Box
                    component="main" sx={{
                        flexGrow: 1, overflow: "auto"
                    }}
                >
                    <ExpressionsControl
                        showPageHeader={true}
                        expressions={sendValidations}
                        updateParentExpressions={updateSendValidations}
                        title="Any 'False' evaluations will Halt a Docutech request"
                    />
                </Box>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={alertOpen}
                    autoHideDuration={4000}
                    onClose={handleSnackBarClose}
                >
                    <MuiAlert
                        onClose={handleSnackBarClose}
                        severity={alertSeverity}
                        sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                        {alertMessage}
                    </MuiAlert>
                </Snackbar>
            </section>
        </>
    );
};

export default ConfigSendValidations;